import axios from "axios";

const baseUrl = `${process.env.REACT_APP_GITO__OCPP201_API_URL}`;
//const baseUrl = "http://localhost:8201";

function getOcppErrorMessage(data, fallbackMessage) {
    if (data == null) {
        return fallbackMessage;
    }

    if (typeof data === "string") {
        const trimmed = data.trim();
        return trimmed !== "" ? trimmed : fallbackMessage;
    }

    if (typeof data.detail === "string" && data.detail.trim() !== "") {
        return data.detail;
    }

    if (typeof data.detail?.message === "string" && data.detail.message.trim() !== "") {
        return data.detail.message;
    }

    if (typeof data.errorDescription === "string" && data.errorDescription.trim() !== "") {
        return data.errorDescription;
    }

    if (typeof data.message === "string" && data.message.trim() !== "") {
        return data.message;
    }

    if (typeof data.errorCode === "string" && data.errorCode.trim() !== "") {
        return data.errorCode;
    }

    return fallbackMessage;
}

function throwOcppApiError(error, fallbackMessage) {
    if (error instanceof Error && !error.response) {
        throw error;
    }

    const message = getOcppErrorMessage(
        error?.response?.data,
        error instanceof Error ? error.message : fallbackMessage
    );
    throw new Error(message || fallbackMessage);
}

function ensureOcppStatus(data, fallbackMessage, allowedStatuses) {
    const status = data?.payload?.status;
    if (allowedStatuses.includes(status)) {
        return;
    }

    throw new Error(getOcppErrorMessage(data, fallbackMessage));
}

export async function put_RemoteManagement_change_availability(apiAccessToken, data) {
    const fallbackMessage = "Failed to call PUT /RemoteManagement/change-availability (OCPP 2.0.1)";

    try {
        const response = await axios.put(`${baseUrl}/RemoteManagement/change-availability`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
        });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted", "Scheduled"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_trigger_message(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/trigger-message (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/trigger-message`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
        });

        const res = response?.data;

        if (res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        const status = res?.payload?.status;
        ensureOcppStatus(res, status ? `Trigger message ${status}` : fallbackMessage, ["Accepted"]);
        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_reset(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/reset (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/reset`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
        });

        const res = response?.data;

        if (res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        ensureOcppStatus(res, fallbackMessage, ["Accepted", "Scheduled"]);
        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_certificates(apiAccessToken, chargingPointPublicId) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/certificates (OCPP 2.0.1)";
    const path = chargingPointPublicId
        ? `${baseUrl}/RemoteManagement/certificates/${encodeURIComponent(chargingPointPublicId)}`
        : `${baseUrl}/RemoteManagement/certificates`;

    try {
        const response = await axios.get(path, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_certificates_installed(apiAccessToken, chargingPointPublicId) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/certificates/installed (OCPP 2.0.1)";
    const params = new URLSearchParams();
    params.append("certificateType", "V2GRootCertificate");
    params.append("certificateType", "V2GCertificateChain");

    try {
        const response = await axios.get(
            `${baseUrl}/RemoteManagement/certificates/${encodeURIComponent(chargingPointPublicId)}/installed?${params}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_hubject_v2g_roots(apiAccessToken, chargingPointPublicId) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/certificates/hubject-v2g-roots (OCPP 2.0.1)";

    try {
        const response = await axios.get(
            `${baseUrl}/RemoteManagement/certificates/${encodeURIComponent(chargingPointPublicId)}/hubject-v2g-roots`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_install_v2g_roots(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/install-v2g-roots (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/install-v2g-roots`, data, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_provision_v2g_certificates(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/provision-v2g-certificates (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/provision-v2g-certificates`, data, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function delete_RemoteManagement_certificates(apiAccessToken, chargingPointPublicId, chargingPointCertificatePublicId) {
    const fallbackMessage = "Failed to call DELETE /RemoteManagement/certificates (OCPP 2.0.1)";

    try {
        const response = await axios.delete(
            `${baseUrl}/RemoteManagement/certificates/${encodeURIComponent(chargingPointPublicId)}/${encodeURIComponent(chargingPointCertificatePublicId)}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );

        const status = response?.data?.status || response?.data?.Status;
        if (!["Accepted", "NotFound"].includes(status)) {
            throw new Error(getOcppErrorMessage(response.data, `Delete certificate ${status || "failed"}`));
        }

        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_hubject_registrations(apiAccessToken) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/hubject-registrations (OCPP 2.0.1)";

    try {
        const response = await axios.get(`${baseUrl}/RemoteManagement/hubject-registrations`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_hubject_registration(apiAccessToken, chargingPointPublicId) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/hubject-registration (OCPP 2.0.1)";

    try {
        const response = await axios.get(
            `${baseUrl}/RemoteManagement/hubject-registration/${encodeURIComponent(chargingPointPublicId)}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_hubject_registration(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/hubject-registration (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/hubject-registration`, data, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function delete_RemoteManagement_hubject_registration(
    apiAccessToken,
    chargingPointPublicId,
    chargingConnectorPublicId
) {
    const fallbackMessage = "Failed to call DELETE /RemoteManagement/hubject-registration (OCPP 2.0.1)";

    try {
        const response = await axios.delete(
            `${baseUrl}/RemoteManagement/hubject-registration/${encodeURIComponent(chargingPointPublicId)}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
                params: chargingConnectorPublicId
                    ? { chargingConnectorPublicId }
                    : undefined,
            }
        );
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_unlock_connector(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/unlock-connector (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/unlock-connector`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
        });

        const res = response?.data;

        if (res?.messageType === "ErrorResponse" || res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_remote_start_charging(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/remote-start-charging (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/remote-start-charging`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
        });

        const res = response?.data;

        if (res?.messageType === "ErrorResponse" || res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        ensureOcppStatus(res, fallbackMessage, ["Accepted"]);
        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_remote_stop_charging(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/remote-stop-charging (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/remote-stop-charging`, data, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiAccessToken}`,
            },
        });

        const res = response?.data;

        if (res?.messageType === "ErrorResponse" || res?.errorCode) {
            throw new Error(getOcppErrorMessage(res, fallbackMessage));
        }

        ensureOcppStatus(res, fallbackMessage, ["Accepted"]);
        return res;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_RemoteManagement_get_diagnostics(apiAccessToken, data) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/get-diagnostics (OCPP 2.0.1)";

    try {
        const response = await axios.get(`${baseUrl}/RemoteManagement/get-diagnostics`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
            params: data,
        });

        const responseData = response?.data;

        if (responseData?.messageType === "ErrorResponse" || responseData?.errorCode) {
            throw new Error(getOcppErrorMessage(responseData, fallbackMessage));
        }

        ensureOcppStatus(responseData, fallbackMessage, ["Accepted", "AcceptedCanceled"]);
        return responseData;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function get_configurations_grouped(apiAccessToken, chargingPointPublicId) {
    const fallbackMessage = "Failed to call GET /RemoteManagement/get-configurations-grouped (OCPP 2.0.1)";

    try {
        const response = await axios.get(
            `${baseUrl}/RemoteManagement/get-configurations-grouped?chargingPointPublicId=${chargingPointPublicId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

const PURPOSE_16_TO_201 = {
    ChargePointMaxProfile: "ChargingStationMaxProfile",
};

export function mapSetChargingProfileTo201(data) {
    const cs = data?.csChargingProfiles ?? {};
    const schedule = cs.chargingSchedule ?? {};
    const purpose16 = cs.chargingProfilePurpose;
    const purpose201 = PURPOSE_16_TO_201[purpose16] ?? purpose16;

    const payload = {
        chargingPointPublicId: data.chargingPointPublicId,
        chargingProfile: {
            stackLevel: cs.stackLevel,
            chargingProfilePurpose: purpose201,
            chargingProfileKind: cs.chargingProfileKind,
            recurrencyKind: cs.recurrencyKind,
            validFrom: cs.validFrom,
            validTo: cs.validTo,
            startSchedule: schedule.startSchedule,
            duration: schedule.duration,
            chargingRateUnit: schedule.chargingRateUnit,
            minChargingRate: schedule.minChargingRate,
            chargingSchedulePeriod: schedule.chargingSchedulePeriod ?? [],
        },
    };

    if (data.chargingConnectorPublicId) {
        payload.chargingConnectorPublicId = data.chargingConnectorPublicId;
    }

    return payload;
}

export async function post_RemoteManagement_set_charging_profile(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/set-charging-profile (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/set-charging-profile`, data, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function post_RemoteManagement_clear_charging_profile(apiAccessToken, data) {
    const fallbackMessage = "Failed to call POST /RemoteManagement/clear-charging-profile (OCPP 2.0.1)";

    try {
        const response = await axios.post(`${baseUrl}/RemoteManagement/clear-charging-profile`, data, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        ensureOcppStatus(response?.data, fallbackMessage, ["Accepted"]);
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function put_change_configurations(apiAccessToken, data) {
    const fallbackMessage = "Failed to call PUT /RemoteManagement/change-configurations (OCPP 2.0.1)";

    try {
        const response = await axios.put(`${baseUrl}/RemoteManagement/change-configurations`, data, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiAccessToken}`,
            },
        });
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}

export async function delete_Reservation_cancel_reservation(apiAccessToken, reservationPublicId) {
    const fallbackMessage = "Failed to call DELETE /Reservation/cancel-reservation (OCPP 2.0.1)";

    try {
        const response = await axios.delete(
            `${baseUrl}/Reservation/cancel-reservation/${reservationPublicId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiAccessToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throwOcppApiError(error, fallbackMessage);
    }
}
